import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { Card } from "@/components/ui/card";
import {
  ArrowLeft, MessageCircleHeart, Heart, ThumbsDown, Star, Loader2,
  TrendingUp, Smile, Meh, Frown, ChevronDown, ChevronRight,
} from "lucide-react";

function StatTile({ label, value, sub, icon: Icon, testid }) {
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-1.5">
        {Icon && <Icon size={12}/>} {label}
      </div>
      <div className="font-display text-3xl text-[#222222]">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </Card>
  );
}

function PacingBar({ pacing }) {
  const total = (pacing?.too_short || 0) + (pacing?.right || 0) + (pacing?.too_long || 0);
  if (!total) return <div className="text-[11px] text-slate-400">No pacing data</div>;
  const pct = (n) => `${Math.round((n / total) * 100)}%`;
  return (
    <div>
      <div className="flex h-2 rounded overflow-hidden bg-slate-100">
        <div className="bg-amber-400" style={{ width: pct(pacing.too_short) }} title={`Too short: ${pacing.too_short}`}/>
        <div className="bg-emerald-500" style={{ width: pct(pacing.right) }} title={`Just right: ${pacing.right}`}/>
        <div className="bg-rose-400" style={{ width: pct(pacing.too_long) }} title={`Too long: ${pacing.too_long}`}/>
      </div>
      <div className="flex justify-between text-[10px] text-slate-500 mt-1">
        <span>{pacing.too_short} short</span><span>{pacing.right} right</span><span>{pacing.too_long} long</span>
      </div>
    </div>
  );
}

export default function AdminFeedback() {
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // expanded learner id
  const [detail, setDetail] = useState({}); // learner_id -> full doc

  useEffect(() => {
    (async () => {
      try {
        const [{ data: s }, { data: rows }] = await Promise.all([
          api.get("/feedback/admin/summary"),
          api.get("/feedback/admin/list"),
        ]);
        setSummary(s);
        setList(rows);
      } catch (_) {
        toast.error("Couldn't load feedback dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleOpen = async (learnerId) => {
    if (open === learnerId) { setOpen(null); return; }
    setOpen(learnerId);
    if (!detail[learnerId]) {
      try {
        const { data } = await api.get(`/feedback/admin/${learnerId}`);
        setDetail((d) => ({ ...d, [learnerId]: data }));
      } catch (_) { /* ignore */ }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 gap-2">
        <Loader2 className="animate-spin" size={16}/> Loading…
      </div>
    );
  }

  const npsDist = summary?.nps_distribution || { promoter: 0, passive: 0, detractor: 0 };
  const totalNps = npsDist.promoter + npsDist.passive + npsDist.detractor;
  const npsScore = totalNps ? Math.round(((npsDist.promoter - npsDist.detractor) / totalNps) * 100) : null;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <Link to="/admin" className="text-sm text-slate-600 hover:text-[#E53935] inline-flex items-center gap-1.5">
            <ArrowLeft size={14}/> Back to admin
          </Link>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#E53935] font-bold mb-2 inline-flex items-center gap-2">
            <MessageCircleHeart size={12}/> Voice of the learner
          </div>
          <h1 className="font-display text-4xl text-[#222222]">Feedback dashboard</h1>
          <p className="text-slate-600 text-sm mt-1">{summary?.submissions || 0} submission{(summary?.submissions === 1) ? "" : "s"} captured.</p>
        </div>

        {summary?.submissions === 0 ? (
          <Card className="p-10 text-center text-slate-500" data-testid="feedback-empty-state">
            No submissions yet. Once learners finish reading every module, their feedback will appear here.
          </Card>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              <StatTile
                testid="stat-submissions"
                label="Submissions" icon={TrendingUp}
                value={summary?.submissions ?? 0}
                sub="Locked, one per learner"
              />
              <StatTile
                testid="stat-nps"
                label="NPS score" icon={Smile}
                value={npsScore === null ? "—" : `${npsScore > 0 ? "+" : ""}${npsScore}`}
                sub={`avg ${summary?.avg_nps ?? "—"} / 10`}
              />
              <StatTile
                testid="stat-promoters"
                label="Promoters (9-10)" icon={Smile}
                value={npsDist.promoter}
                sub={`${npsDist.passive} passive · ${npsDist.detractor} detractors`}
              />
              <StatTile
                testid="stat-overall-relevance"
                label="Overall relevance" icon={Star}
                value={summary?.avg_overall_relevance ? `${summary.avg_overall_relevance} / 5` : "—"}
                sub="Avg of all learners"
              />
            </div>

            {/* Per-module aggregates */}
            <h2 className="font-display text-2xl text-[#222222] mb-4">By module</h2>
            <Card className="p-0 overflow-hidden mb-12" data-testid="feedback-module-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200 text-left">
                    <tr>
                      <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">Module</th>
                      <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">Reviews</th>
                      <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">Avg relevance</th>
                      <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">Pacing</th>
                      <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">Sample comment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(summary?.modules || []).map((m) => (
                      <tr key={m.module_id} className="border-b border-slate-100 align-top">
                        <td className="px-4 py-3">
                          <div className="font-medium text-[#222222]">{m.module_title || "Untitled"}</div>
                          <div className="text-[11px] text-slate-500">{m.module_id.slice(0, 8)}…</div>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{m.count}</td>
                        <td className="px-4 py-3">
                          <span className="font-display text-lg text-[#E53935]">{m.avg_relevance.toFixed(1)}</span>
                          <span className="text-xs text-slate-500"> /5</span>
                        </td>
                        <td className="px-4 py-3 w-40"><PacingBar pacing={m.pacing}/></td>
                        <td className="px-4 py-3 max-w-xs">
                          {(m.comments_better[0] || m.comments_well[0] || m.comments_suggest[0])?.text
                            ? <span className="text-slate-700 line-clamp-2">&ldquo;{(m.comments_better[0] || m.comments_well[0] || m.comments_suggest[0]).text}&rdquo;</span>
                            : <span className="text-slate-400">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Recent overall comments */}
            <h2 className="font-display text-2xl text-[#222222] mb-4">Recent comments</h2>
            <div className="grid md:grid-cols-2 gap-4 mb-12">
              {(summary?.recent_comments || []).map((c, i) => (
                <Card key={i} className="p-5" data-testid={`recent-comment-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-medium text-[#222222]">{c.learner}</div>
                    <div className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">NPS {c.nps}</div>
                  </div>
                  {c.most_loved && (
                    <div className="mb-2">
                      <div className="text-[10px] uppercase tracking-wider text-[#E53935] font-bold flex items-center gap-1 mb-1"><Heart size={10}/> Loved</div>
                      <p className="text-sm text-slate-700">{c.most_loved}</p>
                    </div>
                  )}
                  {c.most_hated && (
                    <div className="mb-2">
                      <div className="text-[10px] uppercase tracking-wider text-slate-600 font-bold flex items-center gap-1 mb-1"><ThumbsDown size={10}/> Hated</div>
                      <p className="text-sm text-slate-700">{c.most_hated}</p>
                    </div>
                  )}
                  {c.suggestions && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-600 font-bold mb-1">Wishlist</div>
                      <p className="text-sm text-slate-700">{c.suggestions}</p>
                    </div>
                  )}
                </Card>
              ))}
            </div>

            {/* Per-learner drill-in */}
            <h2 className="font-display text-2xl text-[#222222] mb-4">All submissions</h2>
            <Card className="overflow-hidden" data-testid="feedback-submissions-list">
              {list.map((row) => (
                <div key={row.learner_id} className="border-b border-slate-100 last:border-0">
                  <button
                    onClick={() => toggleOpen(row.learner_id)}
                    data-testid={`feedback-row-${row.learner_id}`}
                    className="w-full text-left px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {open === row.learner_id ? <ChevronDown size={16} className="text-slate-400"/> : <ChevronRight size={16} className="text-slate-400"/>}
                      <div>
                        <div className="font-medium text-[#222222]">{row.learner_name || row.learner_email}</div>
                        <div className="text-xs text-slate-500">{row.learner_email} · {new Date(row.submitted_at).toLocaleString()}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="px-2 py-0.5 rounded-full bg-slate-100">NPS {row.overall?.nps ?? "—"}</span>
                      <span className="px-2 py-0.5 rounded-full bg-slate-100">{row.overall?.content_relevance ?? "—"}/5</span>
                      <span className="text-slate-500">{row.modules_reviewed}/{row.modules_total} mods</span>
                    </div>
                  </button>
                  {open === row.learner_id && (
                    <div className="px-5 pb-5 pt-1 bg-slate-50" data-testid={`feedback-detail-${row.learner_id}`}>
                      {!detail[row.learner_id] ? (
                        <div className="text-xs text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={12}/> Loading…</div>
                      ) : (
                        <>
                          <div className="grid md:grid-cols-3 gap-3 mb-4">
                            <div><div className="text-[10px] uppercase tracking-wider text-[#E53935] font-bold mb-1">Loved</div><p className="text-sm text-slate-700">{detail[row.learner_id].overall?.most_loved || "—"}</p></div>
                            <div><div className="text-[10px] uppercase tracking-wider text-slate-600 font-bold mb-1">Hated</div><p className="text-sm text-slate-700">{detail[row.learner_id].overall?.most_hated || "—"}</p></div>
                            <div><div className="text-[10px] uppercase tracking-wider text-slate-600 font-bold mb-1">Suggestions</div><p className="text-sm text-slate-700">{detail[row.learner_id].overall?.suggestions || "—"}</p></div>
                          </div>
                          <div className="space-y-2">
                            {(detail[row.learner_id].modules || []).map((m) => (
                              <div key={m.module_id} className="bg-white rounded border border-slate-200 p-3 text-xs">
                                <div className="flex items-center justify-between mb-1.5">
                                  <span className="font-medium text-[#222222]">{m.module_id.slice(0, 8)}…</span>
                                  <span className="text-[10px] text-slate-500">{m.relevance}/5 · {m.pacing}</span>
                                </div>
                                {(m.worked_well || m.could_be_better || m.suggestions) && (
                                  <div className="grid md:grid-cols-3 gap-2 text-slate-700">
                                    <div>{m.worked_well && <><span className="text-emerald-600 font-medium">+ </span>{m.worked_well}</>}</div>
                                    <div>{m.could_be_better && <><span className="text-rose-600 font-medium">- </span>{m.could_be_better}</>}</div>
                                    <div>{m.suggestions && <><span className="text-amber-700 font-medium">~ </span>{m.suggestions}</>}</div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </Card>
          </>
        )}
      </section>
    </div>
  );
}
